/**
 * BackupRestorePage (P3-F Doc 41) — export/import form + workflow JSON.
 */
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
} from "lucide-react";
import * as React from "react";

import {
  type BundleEnvelope,
  type ImportResponse,
  type RestoreMode,
  backupApi,
  downloadBundleAsFile,
} from "@/api/backup";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useUIStore } from "@/stores/ui";

const MODE_OPTIONS: Array<{ value: RestoreMode; label: string; hint: string }> = [
  {
    value: "skip",
    label: "Skip duplicate",
    hint: "Bỏ qua nếu code đã tồn tại (an toàn nhất)",
  },
  {
    value: "overwrite",
    label: "Overwrite",
    hint: "Save làm draft mới đè existing — version cũ vẫn lưu trong history",
  },
  {
    value: "rename",
    label: "Rename",
    hint: "Thêm suffix _imp_<timestamp> nếu trùng",
  },
];

export function BackupRestorePage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [exporting, setExporting] = React.useState<"forms" | "workflows" | null>(
    null,
  );
  const [importType, setImportType] = React.useState<"forms" | "workflows">(
    "forms",
  );
  const [mode, setMode] = React.useState<RestoreMode>("skip");
  const [importing, setImporting] = React.useState(false);
  const [importResult, setImportResult] = React.useState<ImportResponse | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleExport = async (kind: "forms" | "workflows"): Promise<void> => {
    setExporting(kind);
    try {
      const bundle: BundleEnvelope =
        kind === "forms"
          ? await backupApi.exportForms()
          : await backupApi.exportWorkflows();
      const filename = `${kind}-backup-${new Date()
        .toISOString()
        .split("T")[0]}.json`;
      downloadBundleAsFile(bundle, filename);
      pushToast({
        variant: "success",
        title: `Đã export ${bundle.items_count} ${kind}`,
        description: `Filename: ${filename}`,
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Export thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setExporting(null);
    }
  };

  const handleFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text) as Record<string, unknown>;
      const detectedType = bundle.bundle_type as string | undefined;
      if (detectedType !== importType) {
        const proceed = confirm(
          `Bundle type là "${detectedType ?? "unknown"}" nhưng bạn đang import vào "${importType}". Tiếp tục?`,
        );
        if (!proceed) {
          if (fileInputRef.current) fileInputRef.current.value = "";
          setImporting(false);
          return;
        }
      }
      const res =
        importType === "forms"
          ? await backupApi.importForms(bundle, mode)
          : await backupApi.importWorkflows(bundle, mode);
      setImportResult(res);
      pushToast({
        variant: res.items_failed === 0 ? "success" : "warning",
        title: `Import xong: ${res.items_total} items`,
        description: `${res.items_created} created · ${res.items_overwritten} overwritten · ${res.items_skipped} skipped · ${res.items_renamed} renamed · ${res.items_failed} failed`,
      });
    } catch (err) {
      pushToast({
        variant: "destructive",
        title: "Import thất bại",
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <header>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Archive className="size-5 text-primary" />
          Backup & Restore
        </h1>
        <p className="text-sm text-muted-foreground">
          Export form/workflow definitions ra JSON. Import sang tenant khác
          hoặc backup trước migration. Bundle có SHA-256 checksum để verify
          integrity.
        </p>
      </header>

      {/* Export */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Download className="size-4" /> Export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Tải bundle JSON về máy. Mỗi bundle gồm danh sách items + metadata
            + checksum. Forms export latest published version (fallback latest
            draft); workflows tương tự.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="brand"
              onClick={() => void handleExport("forms")}
              disabled={exporting !== null}
            >
              {exporting === "forms" ? (
                <Loader2 className="size-4 mr-1 animate-spin" />
              ) : (
                <Download className="size-4 mr-1" />
              )}
              Export Forms
            </Button>
            <Button
              variant="brand"
              onClick={() => void handleExport("workflows")}
              disabled={exporting !== null}
            >
              {exporting === "workflows" ? (
                <Loader2 className="size-4 mr-1 animate-spin" />
              ) : (
                <Download className="size-4 mr-1" />
              )}
              Export Workflows
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Import */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Upload className="size-4" /> Import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">
              Bundle type
            </label>
            <div className="flex gap-2">
              {(["forms", "workflows"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setImportType(t)}
                  className={
                    "text-xs px-3 py-1.5 rounded-md border transition-colors " +
                    (importType === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted")
                  }
                >
                  {t === "forms" ? "Forms" : "Workflows"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">
              Conflict mode (khi code đã tồn tại)
            </label>
            <div className="space-y-1.5">
              {MODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-start gap-2 cursor-pointer text-xs"
                >
                  <input
                    type="radio"
                    name="mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-medium">{opt.label}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {opt.hint}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={(e) => void handleFileSelected(e)}
              disabled={importing}
              className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:font-medium file:cursor-pointer"
            />
            {importing ? (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Đang import...
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Import result */}
      {importResult ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              {importResult.items_failed === 0 ? (
                <CheckCircle2 className="size-4 text-emerald-600" />
              ) : (
                <AlertTriangle className="size-4 text-amber-600" />
              )}
              Kết quả import
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              <Stat label="Total" value={importResult.items_total} />
              <Stat
                label="Created"
                value={importResult.items_created}
                color="emerald"
              />
              <Stat
                label="Overwritten"
                value={importResult.items_overwritten}
                color="blue"
              />
              <Stat
                label="Skipped"
                value={importResult.items_skipped}
                color="muted"
              />
              <Stat
                label="Failed"
                value={importResult.items_failed}
                color={importResult.items_failed > 0 ? "destructive" : "muted"}
              />
            </div>

            <div className="border rounded-md max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/50">
                  <tr className="text-left">
                    <th className="px-2 py-1.5 font-medium">Code</th>
                    <th className="px-2 py-1.5 font-medium">Action</th>
                    <th className="px-2 py-1.5 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.items.map((item, i) => (
                    <tr
                      key={i}
                      className="border-t hover:bg-muted/30"
                    >
                      <td className="px-2 py-1 font-mono text-[10px]">
                        {item.code}
                      </td>
                      <td className="px-2 py-1">
                        <ActionBadge action={item.action} />
                      </td>
                      <td className="px-2 py-1 text-muted-foreground text-[10px]">
                        {item.error
                          ? item.error
                          : item.new_code
                            ? `→ ${item.new_code}`
                            : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

interface StatProps {
  label: string;
  value: number;
  color?: "emerald" | "blue" | "destructive" | "muted";
}

function Stat({ label, value, color = "muted" }: StatProps): JSX.Element {
  const colorMap = {
    emerald: "text-emerald-700 dark:text-emerald-400",
    blue: "text-blue-700 dark:text-blue-400",
    destructive: "text-destructive",
    muted: "text-foreground",
  };
  return (
    <div className="bg-muted/30 rounded p-2 text-center">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${colorMap[color]}`}>{value}</div>
    </div>
  );
}

function ActionBadge({
  action,
}: {
  action: "created" | "overwritten" | "skipped" | "renamed" | "error";
}): JSX.Element {
  const variants: Record<typeof action, string> = {
    created: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
    overwritten: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100",
    skipped: "bg-muted text-muted-foreground",
    renamed: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
    error: "bg-destructive/10 text-destructive",
  };
  return (
    <Badge className={`${variants[action]} text-[9px] px-1.5 py-0 h-4 font-medium`}>
      {action}
    </Badge>
  );
}
